import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Activity,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Download,
  Building2,
  Clock,
  PieChart,
  AlertCircle,
  UserCheck,
  UserX,
} from 'lucide-react';
import { getUserAnalytics, exportUsersToExcel, UserAnalytics } from '@/services/analyticsService';

export const UserAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<UserAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserAnalytics();
      setData(response);
    } catch (err) {
      setError('Erreur lors du chargement des analyses utilisateurs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportUsersToExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('fr-FR').format(value);
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      SUPER_ADMIN: 'Super Admin',
      INSTITUTION_ADMIN: 'Admin Institution',
      PROFESSOR: 'Professeur',
      STUDENT: 'Etudiant',
      STAFF: 'Personnel',
      PARENT: 'Parent',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      SUPER_ADMIN: 'bg-purple-500',
      INSTITUTION_ADMIN: 'bg-indigo-500',
      PROFESSOR: 'bg-blue-500',
      STUDENT: 'bg-green-500',
      STAFF: 'bg-yellow-500',
      PARENT: 'bg-pink-500',
    };
    return colors[role] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchData}
            className="ml-auto px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const growthRate = data?.newUsersLastMonth
    ? ((data.newUsersThisMonth - data.newUsersLastMonth) / data.newUsersLastMonth) * 100
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utilisateurs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Analyse des utilisateurs de la plateforme
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Download size={16} />
            {exporting ? 'Export...' : 'Exporter Excel'}
          </button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Users */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total utilisateurs</p>
              <p className="text-3xl font-bold mt-1">
                {formatNumber(data?.totalUsers || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Users className="text-white" size={24} />
            </div>
          </div>
        </div>

        {/* New This Month */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Nouveaux ce mois</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatNumber(data?.newUsersThisMonth || 0)}
              </p>
              <p className={`text-sm mt-2 flex items-center gap-1 ${
                growthRate >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {growthRate >= 0 ? (
                  <TrendingUp size={14} />
                ) : (
                  <TrendingDown size={14} />
                )}
                {growthRate >= 0 ? '+' : ''}{growthRate.toFixed(1)}%
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <UserPlus className="text-green-600" size={20} />
            </div>
          </div>
        </div>

        {/* Active 24h */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Actifs (24h)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatNumber(data?.activeUsersLast24h || 0)}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Connectés aujourd'hui
              </p>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Activity className="text-yellow-600" size={20} />
            </div>
          </div>
        </div>

        {/* Active 30d */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Actifs (30j)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatNumber(data?.activeUsersLast30d || 0)}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {data?.totalUsers ? ((data.activeUsersLast30d / data.totalUsers) * 100).toFixed(1) : 0}% du total
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="text-purple-600" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Activation & Retention Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* User Activation Rate */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Taux d'Activation</p>
              <p className={`text-2xl font-bold mt-1 ${
                (data?.userActivationRate || 0) >= 70 ? 'text-green-600' :
                (data?.userActivationRate || 0) >= 40 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {(data?.userActivationRate || 0).toFixed(1)}%
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Nouveaux utilisateurs connectés (30j)
              </p>
            </div>
            <div className={`w-10 h-10 ${
              (data?.userActivationRate || 0) >= 70 ? 'bg-green-100' : 'bg-yellow-100'
            } rounded-lg flex items-center justify-center`}>
              <UserCheck className={
                (data?.userActivationRate || 0) >= 70 ? 'text-green-600' : 'text-yellow-600'
              } size={20} />
            </div>
          </div>
        </div>

        {/* Inactive Users */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Utilisateurs Inactifs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatNumber(data?.inactiveUsers || 0)}
              </p>
              <p className={`text-sm mt-2 ${
                (data?.inactiveRate || 0) <= 20 ? 'text-green-600' :
                (data?.inactiveRate || 0) <= 40 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {(data?.inactiveRate || 0).toFixed(1)}% du total
              </p>
            </div>
            <div className={`w-10 h-10 ${
              (data?.inactiveRate || 0) <= 20 ? 'bg-green-100' : 'bg-red-100'
            } rounded-lg flex items-center justify-center`}>
              <UserX className={
                (data?.inactiveRate || 0) <= 20 ? 'text-green-600' : 'text-red-600'
              } size={20} />
            </div>
          </div>
        </div>

        {/* D7 Retention */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Rétention D7</p>
              <p className={`text-2xl font-bold mt-1 ${
                (data?.retention?.d7 || 0) >= 50 ? 'text-green-600' :
                (data?.retention?.d7 || 0) >= 25 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {(data?.retention?.d7 || 0).toFixed(1)}%
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Reviennent après 7 jours
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-blue-600" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Role */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Répartition par rôle</h3>
            <PieChart className="text-gray-400" size={20} />
          </div>
          {data?.usersByRole && data.usersByRole.length > 0 ? (
            <div className="space-y-4">
              {data.usersByRole.map((item) => {
                const percentage = data.totalUsers
                  ? (item.count / data.totalUsers) * 100
                  : 0;
                return (
                  <div key={item.role} className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${getRoleColor(item.role)}`} />
                    <span className="flex-1 text-gray-700">{getRoleLabel(item.role)}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${getRoleColor(item.role)}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-16 text-right">
                        {formatNumber(item.count)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="mx-auto text-gray-400 mb-2" size={24} />
              <p className="text-gray-500">Aucun utilisateur</p>
            </div>
          )}
        </div>

        {/* Top Institutions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Top établissements</h3>
            <Building2 className="text-gray-400" size={20} />
          </div>
          {data?.topInstitutionsByUsers && data.topInstitutionsByUsers.length > 0 ? (
            <div className="space-y-4">
              {data.topInstitutionsByUsers.map((item, index) => (
                <div
                  key={item.institutionId}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    index === 0 ? 'bg-yellow-100 text-yellow-600' :
                    index === 1 ? 'bg-gray-200 text-gray-600' :
                    index === 2 ? 'bg-orange-100 text-orange-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {item.institutionName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      {formatNumber(item.userCount)}
                    </p>
                    <p className="text-xs text-gray-500">utilisateurs</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="mx-auto text-gray-400 mb-2" size={24} />
              <p className="text-gray-500">Aucun établissement</p>
            </div>
          )}
        </div>
      </div>

      {/* Engagement Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-6">Engagement utilisateurs</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* DAU */}
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-600 font-medium">DAU</p>
            <p className="text-2xl font-bold text-blue-900 mt-1">
              {formatNumber(data?.activeUsersLast24h || 0)}
            </p>
            <p className="text-xs text-blue-600 mt-1">Utilisateurs actifs par jour</p>
          </div>

          {/* WAU */}
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600 font-medium">WAU</p>
            <p className="text-2xl font-bold text-green-900 mt-1">
              {formatNumber(data?.activeUsersLast7d || 0)}
            </p>
            <p className="text-xs text-green-600 mt-1">Utilisateurs actifs par semaine</p>
          </div>

          {/* MAU */}
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-600 font-medium">MAU</p>
            <p className="text-2xl font-bold text-purple-900 mt-1">
              {formatNumber(data?.activeUsersLast30d || 0)}
            </p>
            <p className="text-xs text-purple-600 mt-1">Utilisateurs actifs par mois</p>
          </div>
        </div>

        {/* DAU/MAU Ratio */}
        {data?.activeUsersLast30d && data.activeUsersLast30d > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Ratio DAU/MAU (Stickiness)</p>
                <p className="text-xs text-gray-500 mt-1">
                  Mesure l'engagement régulier des utilisateurs
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">
                  {((data.activeUsersLast24h / data.activeUsersLast30d) * 100).toFixed(1)}%
                </p>
                <p className={`text-xs ${
                  (data.activeUsersLast24h / data.activeUsersLast30d) >= 0.2
                    ? 'text-green-600'
                    : 'text-yellow-600'
                }`}>
                  {(data.activeUsersLast24h / data.activeUsersLast30d) >= 0.2
                    ? 'Bon engagement'
                    : 'A améliorer'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAnalyticsPage;
